import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  Activity,
  TrendingUp,
  DollarSign,
  Zap,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { isPlatformOrg, isSuperAdmin } from "@/lib/roles";
import { automationApi } from "@/services/api/automation";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/portal/automation/monitoring")({
  component: AutomationMonitoring,
});

interface HealthData {
  total_providers: number;
  healthy_providers: number;
  degraded_providers: number;
  blocked_providers: number;
  overall_success_rate: number;
  average_latency_ms: number | null;
  recent_errors_1h: number;
  providers: Array<{
    provider_id: string;
    name: string;
    status: string;
    success_rate: number | null;
    avg_latency_ms: number | null;
    is_in_cooldown: boolean;
  }>;
  last_updated: string;
}

interface MetricsData {
  total_requests_24h: number;
  total_requests_1h: number;
  success_rate_24h: number;
  total_cost_24h: number;
  active_providers: number;
  quorum_decisions_24h: number;
  requests_per_hour: number;
  average_cost_per_request: number;
  timestamp: string;
}

interface CostsData {
  analysis_period_days: number;
  total_cost: number;
  total_requests: number;
  average_cost_per_request: number;
  cost_by_provider: Record<string, { cost: number; requests: number }>;
  cost_by_task_type: Record<string, { cost: number; requests: number }>;
  daily_cost_trend: Array<{ date: string; cost: number }>;
  last_updated: string;
}

function AutomationMonitoring() {
  const { token, session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const showCosts = isSuperAdmin(session);

  const [health, setHealth] = useState<HealthData | null>(null);
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [costs, setCosts] = useState<CostsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<
    "day" | "week" | "month"
  >("day");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (token) {
      loadMonitoringData();

      // Set up auto-refresh every 30 seconds
      const interval = setInterval(() => {
        loadMonitoringData();
      }, 30000);

      return () => {
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPeriod]);

  // Monitoring requires SuperAdmin + platform org selected
  if (!isSuperAdmin(session) || !isPlatformOrg(selectedOrganisation)) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Access Restricted</h3>
            <p className="text-muted-foreground mt-2">
              Monitoring is available to platform administrators on the platform organisation only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const loadMonitoringData = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      // Only fetch cost data for SuperAdmins
      const promises: Promise<unknown>[] = [
        automationApi.monitoring.getHealth(token),
        automationApi.monitoring.getMetrics(token),
      ];
      if (showCosts) {
        promises.push(automationApi.monitoring.getCosts(token, selectedPeriod));
      }

      const results = await Promise.all(promises);
      setHealth(results[0] as HealthData);
      setMetrics(results[1] as MetricsData);
      if (showCosts && results[2]) {
        setCosts(results[2] as CostsData);
      }
    } catch (err) {
      console.error("Failed to load monitoring data:", err);
      setError("Failed to load monitoring data");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "degraded":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "blocked":
      case "disabled":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadgeVariant = (
    status: string,
  ): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case "active":
        return "default";
      case "degraded":
        return "secondary";
      case "blocked":
      case "disabled":
        return "destructive";
      default:
        return "outline";
    }
  };

  const formatCurrency = (amount: number | string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(Number(amount || 0));
  };

  const formatNumber = (num: number | string) => {
    return new Intl.NumberFormat("en-US").format(Number(num || 0));
  };

  const formatPercent = (num: number) => {
    return `${(num * 100).toFixed(1)}%`;
  };

  const getOverallStatus = () => {
    if (!health) return "unknown";
    if (health.blocked_providers > 0) return "degraded";
    if (health.degraded_providers > 0) return "degraded";
    if (
      health.healthy_providers === health.total_providers &&
      health.total_providers > 0
    )
      return "healthy";
    if (health.total_providers === 0) return "no providers";
    return "unknown";
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Automation Monitoring</h1>
          <p className="mt-2 text-lg text-slate-600">
            Monitor LLM provider health, performance metrics, and costs
          </p>
        </div>
        <Select
          value={selectedPeriod}
          onValueChange={(value: "day" | "week" | "month") =>
            setSelectedPeriod(value)
          }
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Last 24h</SelectItem>
            <SelectItem value="week">Last 7 days</SelectItem>
            <SelectItem value="month">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="metrics">Performance</TabsTrigger>
          {showCosts && <TabsTrigger value="costs">Costs & Usage</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Quick Stats Grid */}
          <div className={`grid grid-cols-1 gap-4 ${showCosts ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    System Status
                  </CardTitle>
                  {getStatusIcon(
                    getOverallStatus() === "healthy" ? "active" : "degraded",
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold capitalize">
                  {getOverallStatus()}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {health?.total_providers || 0} providers configured
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    Request Rate
                  </CardTitle>
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metrics
                    ? `${metrics.requests_per_hour.toFixed(1)}/hr`
                    : "0/hr"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics ? formatNumber(metrics.total_requests_24h) : "0"}{" "}
                  requests in 24h
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    Success Rate
                  </CardTitle>
                  <Zap className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metrics ? formatPercent(metrics.success_rate_24h) : "0%"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {health?.recent_errors_1h || 0} errors in last hour
                </p>
              </CardContent>
            </Card>

            {showCosts && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">
                      Total Cost
                    </CardTitle>
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {costs ? formatCurrency(costs.total_cost) : "$0.00"}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedPeriod === "day"
                      ? "Last 24 hours"
                      : selectedPeriod === "week"
                        ? "Last 7 days"
                        : "Last 30 days"}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Provider Health Summary */}
          <Card>
            <CardHeader>
              <CardTitle>LLM Provider Health</CardTitle>
              <CardDescription>
                Current status of all configured LLM providers
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Loading provider status...
                  </p>
                </div>
              ) : health && health.providers.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {health.providers.map((provider) => (
                    <div
                      key={provider.provider_id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(provider.status)}
                        <div>
                          <span className="font-medium">{provider.name}</span>
                          {provider.is_in_cooldown && (
                            <div className="flex items-center text-xs text-yellow-600">
                              <Clock className="h-3 w-3 mr-1" />
                              Cooldown
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge variant={getStatusBadgeVariant(provider.status)}>
                        {provider.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No providers configured
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Provider Details</CardTitle>
              <CardDescription>
                Detailed health and performance metrics for each provider
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {health && health.providers.length > 0 ? (
                health.providers.map((provider) => (
                  <div
                    key={provider.provider_id}
                    className="border rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {getStatusIcon(provider.status)}
                        <div>
                          <h4 className="font-medium">{provider.name}</h4>
                          <div className="text-sm text-muted-foreground mt-1 space-y-1">
                            <p>
                              Success Rate:{" "}
                              {provider.success_rate !== null
                                ? formatPercent(provider.success_rate)
                                : "N/A"}
                            </p>
                            <p>
                              Avg Latency:{" "}
                              {provider.avg_latency_ms !== null
                                ? `${provider.avg_latency_ms}ms`
                                : "N/A"}
                            </p>
                            {provider.is_in_cooldown && (
                              <p className="text-yellow-600">
                                Currently in cooldown
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <Badge variant={getStatusBadgeVariant(provider.status)}>
                        {provider.status}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    No providers configured
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Request Metrics (24h)</CardTitle>
                <CardDescription>API request statistics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {metrics ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Total Requests</span>
                      <span className="font-medium">
                        {formatNumber(metrics.total_requests_24h)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Requests (Last Hour)</span>
                      <span className="font-medium">
                        {formatNumber(metrics.total_requests_1h)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Request Rate</span>
                      <span className="font-medium">
                        {metrics.requests_per_hour.toFixed(1)}/hr
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Success Rate</span>
                      <span className="font-medium">
                        {formatPercent(metrics.success_rate_24h)}
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No metrics available
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Overview</CardTitle>
                <CardDescription>
                  Provider and quorum statistics
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {metrics ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Active Providers</span>
                      <span className="font-medium">
                        {metrics.active_providers}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Quorum Decisions (24h)</span>
                      <span className="font-medium">
                        {formatNumber(metrics.quorum_decisions_24h)}
                      </span>
                    </div>
                    {showCosts && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Avg Cost per Request</span>
                          <span className="font-medium">
                            {formatCurrency(metrics.average_cost_per_request)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Total Cost (24h)</span>
                          <span className="font-medium">
                            {formatCurrency(metrics.total_cost_24h)}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No metrics available
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Average Latency Card */}
          {health && health.average_latency_ms !== null && (
            <Card>
              <CardHeader>
                <CardTitle>Average Latency</CardTitle>
                <CardDescription>
                  Overall provider response time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {health.average_latency_ms}ms
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Based on {health.total_providers} provider(s)
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {showCosts && (
          <TabsContent value="costs" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Cost by Provider</CardTitle>
                  <CardDescription>
                    Breakdown of costs per LLM provider
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {costs && Object.keys(costs.cost_by_provider).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(costs.cost_by_provider).map(
                        ([provider, data]) => (
                          <div
                            key={provider}
                            className="flex justify-between items-center"
                          >
                            <div>
                              <span className="text-sm">{provider}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                ({formatNumber(data.requests)} requests)
                              </span>
                            </div>
                            <span className="font-medium">
                              {formatCurrency(data.cost)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">
                      No cost data available
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Cost by Task Type</CardTitle>
                  <CardDescription>
                    Breakdown of costs per task category
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {costs && Object.keys(costs.cost_by_task_type).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(costs.cost_by_task_type).map(
                        ([taskType, data]) => (
                          <div
                            key={taskType}
                            className="flex justify-between items-center"
                          >
                            <div>
                              <span className="text-sm capitalize">
                                {taskType.replace(/_/g, " ")}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                ({formatNumber(data.requests)} requests)
                              </span>
                            </div>
                            <span className="font-medium">
                              {formatCurrency(data.cost)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">
                      No task cost data available
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Cost Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Cost Summary</CardTitle>
                <CardDescription>
                  {costs
                    ? `Last ${costs.analysis_period_days} days`
                    : "Cost overview"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Cost</p>
                    <p className="text-2xl font-bold">
                      {costs ? formatCurrency(costs.total_cost) : "$0.00"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Requests
                    </p>
                    <p className="text-2xl font-bold">
                      {costs ? formatNumber(costs.total_requests) : "0"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Avg Cost/Request
                    </p>
                    <p className="text-2xl font-bold">
                      {costs
                        ? formatCurrency(costs.average_cost_per_request)
                        : "$0.00"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Last Updated</p>
                    <p className="text-lg font-medium">
                      {costs
                        ? new Date(costs.last_updated).toLocaleTimeString()
                        : "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
